import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { OpportunityInvestmentProjectionProps } from '../../lib/types'
import { H5 } from '../../lib/components'
import OpportunityInvestmentProjectionChart from './opportunity-detail-investment-projection-chart'
import OpportunityInvestmentProjectionInput from './opportunity-detail-investment-projection-input'
import OpportunityInvestmentProjectionSummary from './opportunity-detail-investment-projection-summary'
import {
  DEFAULT_SLIDER_MIN,
  OPPORTUNITY_SECTION_IDS,
  buildProjectionSeries,
  formatProjectionRangeLabel,
  getAmountSliderMax,
  getAmountSliderStep,
  getProjectionRateRange,
  getRangeMonths,
  toCurrencyLabel,
} from './opportunity-detail.utils'

const OpportunityInvestmentProjection = ({
  project,
  expectedReturn,
}: OpportunityInvestmentProjectionProps) => {
  const minimumInvestmentAmount = useMemo(
    () => (project?.ticketSize ? Math.round(parseFloat(project.ticketSize) / 100) : 0),
    [project?.ticketSize],
  )
  const sliderMin = useMemo(
    () => (minimumInvestmentAmount > 0 ? minimumInvestmentAmount : DEFAULT_SLIDER_MIN),
    [minimumInvestmentAmount],
  )
  const sliderStep = useMemo(() => getAmountSliderStep(sliderMin), [sliderMin])
  const sliderMax = useMemo(
    () => getAmountSliderMax(sliderMin, sliderStep),
    [sliderMin, sliderStep],
  )
  const [startingAmount, setStartingAmount] = useState(sliderMin)
  const [selectedRange, setSelectedRange] = useState<string>('max')

  useEffect(() => {
    setStartingAmount(sliderMin)
  }, [sliderMin])

  const totalProjectionMonths = getRangeMonths('max')
  const cmsTargetReturn = expectedReturn?.trim() || undefined
  const { minRatePercent, maxRatePercent } = getProjectionRateRange(cmsTargetReturn)
  const expectedRatePercent = maxRatePercent > 0 ? (minRatePercent + maxRatePercent) / 2 : 0
  const hasProjection = startingAmount > 0 && expectedRatePercent > 0
  const projectionSeries = buildProjectionSeries({
    startingAmount,
    annualRatePercent: expectedRatePercent,
    totalMonths: totalProjectionMonths,
  })
  const selectedRangeMonths =
    selectedRange === 'max'
      ? totalProjectionMonths
      : Math.min(parseInt(selectedRange.split(' ')[0], 10), totalProjectionMonths)
  const filteredProjectionSeries = projectionSeries.filter(
    (point) => point.monthOffset <= selectedRangeMonths,
  )
  const selectedProjectionPoint =
    filteredProjectionSeries[filteredProjectionSeries.length - 1] ??
    projectionSeries[projectionSeries.length - 1]
  const selectedProjectedIncome = selectedProjectionPoint.value - startingAmount
  const selectedRangeLabel = formatProjectionRangeLabel(selectedRangeMonths)
  const expectedReturnLabel = cmsTargetReturn || 'Available soon'
  const potentialTotalValueLabel = hasProjection
    ? toCurrencyLabel(selectedProjectionPoint.value, project?.currencyCode)
    : 'Available soon'
  const selectedProjectedIncomeLabel = hasProjection
    ? toCurrencyLabel(selectedProjectedIncome, project?.currencyCode)
    : 'Available soon'

  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStartingAmount(Number(event.target.value))
  }

  return (
    <section
      id={OPPORTUNITY_SECTION_IDS.investmentProjection}
      className='opportunity-overview-panel opportunity-investment-projection-card'
    >
      <div className='opportunity-investment-projection-header'>
        <div>
          <H5 text='What Your Investment Could Become' />
          <p className='opportunity-investment-projection-copy'>
            Use the slider to choose a starting amount and see an illustrative estimate based on the
            expected rate of return for this opportunity.
          </p>
        </div>
        <OpportunityInvestmentProjectionInput
          currencyCode={project?.currencyCode}
          ticketSize={project?.ticketSize}
          sliderMin={sliderMin}
          sliderMax={sliderMax}
          sliderStep={sliderStep}
          startingAmount={startingAmount}
          onAmountChange={handleAmountChange}
        />
      </div>

      <OpportunityInvestmentProjectionSummary
        expectedReturnLabel={expectedReturnLabel}
        selectedRangeLabel={selectedRangeLabel}
        potentialTotalValueLabel={potentialTotalValueLabel}
      />

      <OpportunityInvestmentProjectionChart
        currencyCode={project?.currencyCode}
        filteredProjectionSeries={filteredProjectionSeries}
        hasProjection={hasProjection}
        onRangeChange={setSelectedRange}
        selectedProjectedIncomeLabel={selectedProjectedIncomeLabel}
        selectedProjectionValueLabel={potentialTotalValueLabel}
        selectedRange={selectedRange}
        selectedRangeLabel={selectedRangeLabel}
        startingAmount={startingAmount}
      />

      <p className='opportunity-investment-projection-footnote'>
        This is an illustrative estimate based on the expected rate of return and the selected
        range. It is not a guarantee of future performance or returns.
      </p>
    </section>
  )
}

export default OpportunityInvestmentProjection
